import { COLORS, OUTDATED, REQUEST_STATUS, ROLES } from "@src/constants";
import RequestedRoleRenderer from "@src/custom_renderer/requested_role_renderer/requested_role_renderer";
import { StatusRenderer } from "@src/custom_renderer/status_renderer";
import { FrameworkComponents } from "@src/types/aggrid_types";
import {
  AdapterCellRendererProps,
  RequestedRoleRendererValue,
  StatusRendererValue,
} from "@src/types/custom_renderer_types";
import { ResourcePlanTableRowData } from "@src/types/role_request_types";
import {
  columnTypes,
  paginationGridOptions,
  preventPinnedColumnJumpingGridOption,
} from "@src/utils/aggrid_utils";
import { getAllocationColumnDefs } from "@src/utils/allocation_column_def_utils";
import {
  editableColumnDef,
  editableGridOptions,
  editAllocationCellClassRules,
  editAllocationValueGetter,
  editAllocationValueSetter,
} from "@src/utils/edit_allocation_utils";
import {
  ColDef,
  GetQuickFilterTextParams,
  GetRowIdParams,
  GridOptions,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { ExcelStyle } from "ag-grid-community/dist/types/src/interfaces/iExcelCreator";

const excelStyles: ExcelStyle[] = [
  {
    id: "header",
    alignment: {
      vertical: "Center",
    },
    interior: {
      color: COLORS.SAND_40,
      pattern: "Solid",
    },
    font: {
      bold: true,
      size: 13,
    },
    borders: {
      borderBottom: {
        color: COLORS.SAND_70,
        lineStyle: "Continuous",
        weight: 1,
      },
      borderRight: {
        color: COLORS.SAND_70,
        lineStyle: "Continuous",
        weight: 1,
      },
    },
  },
];

export function getGridOptions(): GridOptions {
  return {
    ...editableGridOptions,
    ...paginationGridOptions,
    ...preventPinnedColumnJumpingGridOption,
    getRowId: (params: GetRowIdParams<ResourcePlanTableRowData>) => {
      return params.data.roleRequestId.toString();
    },
    rowHeight: 50,
    domLayout: "autoHeight",
    suppressContextMenu: true,
    groupHeaderHeight: 26,
    headerHeight: 26,
    components: getComponents(),
    excelStyles: excelStyles,
    defaultExcelExportParams: {
      freezeRows: "headers",
    },
    autoSizeStrategy: {
      type: "fitCellContents",
    },
    statusBar: {
      statusPanels: [{ statusPanel: "agFilteredRowCountComponent" }],
    },
    defaultColDef: {
      ...columnTypes.default,
      cellClass: "no-focus-border",
    },
    context: {
      view: ROLES.PROJECT_LEAD,
    },
    rowSelection: {
      mode: "multiRow",
      selectAll: "filtered",
    },
    selectionColumnDef: {
      pinned: "left",
      maxWidth: 50,
    },
  };
}

function getComponents(): FrameworkComponents {
  return {
    requestedRoleRenderer: (props: AdapterCellRendererProps) => {
      return <RequestedRoleRenderer props={props} />;
    },
    statusRenderer: (props: AdapterCellRendererProps) => {
      return <StatusRenderer props={props} />;
    },
  };
}

/**
 * Get column definitions for the resource plan table
 * @param endDate
 */
export function getColumnDefs(
  endDate: Date
): ColDef<ResourcePlanTableRowData>[] {
  return [
    {
      field: "roleRequestId",
      headerName: "Request id",
      pinned: "left",
      hide: true,
    },
    {
      field: "technologies",
      headerName: "Technology",
      pinned: "left",
      hide: true,
    },
    {
      field: "standardRoleName",
      headerName: "Requested Role",
      cellRenderer: "requestedRoleRenderer",
      cellRendererParams: {
        adapter: (
          rowData: ResourcePlanTableRowData
        ): RequestedRoleRendererValue => ({
          roleRequestId: rowData.roleRequestId,
          standardRoleName: rowData.standardRoleName,
          basicRoleName: rowData.basicRoleName,
          roleSpecificationName: rowData.roleSpecificationName,
          actionRequired: rowData.actionRequired,
          isUpdated: rowData.isUpdated,
          isCommentAvailable:
            rowData.isCommentAvailable && !rowData.isCommentRead,
          latestComment: rowData.latestComment,
          isJointVenture: rowData.jointVenture,
          isTeamLeadAssigned: !!rowData.teamLead,
          isDraft: rowData.status === REQUEST_STATUS.DRAFT,
        }),
      },
      pinned: "left",
      filter: "agSetColumnFilter",
      suppressFillHandle: true,
      getQuickFilterText: (
        params: GetQuickFilterTextParams<ResourcePlanTableRowData>
      ) => {
        return `${params.data.roleRequestId} ${params.data.standardRoleName}`;
      },
    },
    {
      field: "jointVenture",
      headerName: "Joint Venture",
      pinned: "left",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      field: "actionRequired",
      headerName: "Action Required",
      pinned: "left",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      field: "isDeclined",
      headerName: "Declined by Project",
      pinned: "left",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      field: "isCommentAvailable",
      headerName: "Comment Available",
      pinned: "left",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      field: "projectIdentifier",
      headerName: "Project Identifier",
      pinned: "left",
      suppressFillHandle: true,
    },
    {
      field: "specificRole",
      headerName: "Specific Role",
      pinned: "left",
      suppressFillHandle: true,
    },
    {
      field: "scope",
      headerName: "Scope",
      pinned: "left",
      suppressFillHandle: true,
    },
    {
      field: "status",
      headerName: "Status",
      cellRenderer: "statusRenderer",
      cellRendererParams: {
        adapter: (rowData: ResourcePlanTableRowData): StatusRendererValue => ({
          status: rowData.status,
          assignedEmployeeFullName: rowData.assignedEmployee?.fullName,
          isOutdated: rowData.isOutdated,
          daysUntilDeletion: rowData.daysUntilDeletion,
          isJointVentureRequest: rowData.jointVenture,
          jointVenturePartner: rowData.jointVenturePartner,
        }),
      },
      valueGetter: (params: ValueGetterParams) =>
        params.data.isOutdated ? OUTDATED : params.data.status,
      getQuickFilterText: (
        params: GetQuickFilterTextParams<ResourcePlanTableRowData>
      ) => {
        const status = params.data.isOutdated ? OUTDATED : params.data.status;
        return `${status} ${params.data.assignedEmployee?.fullName}`;
      },
      pinned: "left",
      filter: "agSetColumnFilter",
      suppressFillHandle: true,
    },
    ...getTeamMemberColumnDefs(),
    ...getTeamLeadColumns(),
    ...getAllocationColumnDefs(new Date(), endDate, getAllocationColDef),
  ];
}

/**
 *  Get team member column definitions. These columns are hidden by default and are used for exporting the data to excel.
 */
function getTeamMemberColumnDefs(): ColDef<ResourcePlanTableRowData>[] {
  return [
    {
      field: "assignedEmployee.fullName",
      headerName: "Team member",
      pinned: "left",
      hide: true,
    },
    {
      field: "assignedEmployee.contractType",
      headerName: "Contract type",
      pinned: "left",
      hide: true,
    },
    {
      field: "assignedEmployee.totalAvailablePercentage",
      headerName: "Contract availability",
      pinned: "left",
      hide: true,
    },
    {
      field: "assignedEmployee.rcdNumber",
      headerName: "RCD Team member",
      pinned: "left",
      hide: true,
    },
    {
      field: "assignedEmployee.uiNumber",
      headerName: "UI Team member",
      pinned: "left",
      hide: true,
    },
    {
      field: "assignedEmployee.departmentCode",
      headerName: "Team member Org-Code",
      pinned: "left",
      hide: true,
    },
  ];
}

/**
 * Get team lead column definitions. These columns are hidden by default and are used for exporting the data to excel.
 */
function getTeamLeadColumns(): ColDef<ResourcePlanTableRowData>[] {
  return [
    {
      field: "teamLead.fullName",
      headerName: "Team lead",
      pinned: "left",
      hide: true,
    },
    {
      field: "teamLead.rcdNumber",
      headerName: "RCD Team lead",
      pinned: "left",
      hide: true,
    },
    {
      field: "teamLead.uiNumber",
      headerName: "UI Team lead",
      pinned: "left",
      hide: true,
    },
    {
      field: "teamLead.departmentCode",
      headerName: "Team lead Org-Code",
      pinned: "left",
      hide: true,
    },
    {
      field: "teamLead.costCenter",
      headerName: "Cost center",
      pinned: "left",
      hide: true,
    },
  ];
}

function getAllocationColDef(year: number, month: number): ColDef {
  return {
    ...editableColumnDef,
    enableCellChangeFlash: true,
    cellClassRules: editAllocationCellClassRules,
    valueSetter: (params: ValueSetterParams<ResourcePlanTableRowData>) =>
      editAllocationValueSetter(params, year, month),
    valueGetter: (params: ValueGetterParams<ResourcePlanTableRowData>) =>
      editAllocationValueGetter(params, year, month),
  };
}
